'use client';

import { useProject } from '@/contexts/ProjectContext';
import { Project, useProjects } from '@/hooks/useProjects';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import ProjectModal from './ProjectModal';

export default function ProjectNavigation() {
  const queryClient = useQueryClient();
  const { data: projects = [], isLoading: loading } = useProjects();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const { selectedProject, setSelectedProject, setSelectedSprint, selectedDashboard, setSelectedDashboard } = useProject();
  
  // Restore dashboard from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && !selectedDashboard && !selectedProject) {
      const savedDashboard = localStorage.getItem('selected_dashboard');
      if (savedDashboard && (savedDashboard === 'gtm' || savedDashboard === 'customer' || savedDashboard === 'product')) {
        setSelectedDashboard(savedDashboard as 'gtm' | 'customer' | 'product');
      }
    }
  }, []); // Only run on mount

  useEffect(() => {
    // Don't auto-select project if a dashboard is selected
    if (selectedDashboard) {
      return;
    }
    
    // Restore saved project or auto-select first project if none selected
    if (!selectedProject && projects.length > 0) {
      // Try to restore saved project ID from localStorage
      if (typeof window !== 'undefined') {
        const savedProjectId = localStorage.getItem('selected_project_id');
        if (savedProjectId) {
          const projectId = parseInt(savedProjectId, 10);
          const savedProject = projects.find(p => p.id === projectId);
          if (savedProject) {
            setSelectedProject(savedProject);
            return;
          }
        }
      }
      // Fallback to first project if no saved project found
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject, setSelectedProject, selectedDashboard]);

  const handleProjectCreated = () => {
    // Refresh projects list after creating a new one
    queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  return (
    <>
      <div className="w-full bg-slate-800/40 backdrop-blur-sm border-b border-slate-700/50 px-6 py-3 flex items-center gap-4">
        {/* Business Operations Dashboards - Fixed, Styled */}
        <div className="flex items-center gap-1 mr-4">
          <button
            onClick={() => {
              setSelectedDashboard('gtm');
              setSelectedProject(null);
              setSelectedSprint(null);
            }}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selectedDashboard === 'gtm'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 border border-blue-500/30'
            }`}
          >
            <span>📣</span>
            <span>GTM</span>
          </button>
          <button
            onClick={() => {
              setSelectedDashboard('customer');
              setSelectedProject(null);
              setSelectedSprint(null);
            }}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selectedDashboard === 'customer'
                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30 border border-emerald-500/30'
            }`}
          >
            <span>👥</span>
            <span>Customer</span>
          </button>
          <button
            onClick={() => {
              setSelectedDashboard('product');
              setSelectedProject(null);
              setSelectedSprint(null);
            }}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selectedDashboard === 'product'
                ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/30'
                : 'bg-violet-600/20 text-violet-300 hover:bg-violet-600/30 border border-violet-500/30'
            }`}
          >
            <span>🗺️</span>
            <span>Product</span>
          </button>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-slate-600 mr-4"></div>

        {/* User Projects - Standard Tabs */}
        {loading ? (
          <div className="text-gray-400 text-sm">Loading projects...</div>
        ) : projects.length > 0 ? (
          projects.map((project) => (
            <div
              key={project.id}
              className="group relative inline-flex items-center"
            >
              <button
                onClick={() => {
                  setSelectedProject(project);
                  setSelectedSprint(null); // Clear kanban board when switching projects
                  setSelectedDashboard(null); // Clear dashboard when selecting project
                }}
                className={`px-4 py-2 pr-8 rounded-lg font-semibold text-sm transition-all ${
                  selectedProject?.id === project.id
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/30'
                    : 'bg-violet-600/20 text-violet-300 hover:bg-violet-600/30 border border-violet-500/30'
                }`}
              >
                {project.name}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingProject(project);
                  setIsModalOpen(true);
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-[#2d1b3d] rounded"
                title="Edit project"
              >
                <svg className="w-2.5 h-2.5 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          ))
        ) : (
          <div className="text-gray-400 text-sm">No projects yet</div>
        )}

        {/* New Project Button - Right Side */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="ml-auto px-4 py-2 bg-indigo-500/10 backdrop-blur-sm border border-indigo-500/30 hover:bg-indigo-500/20 rounded-lg text-blue-50 font-semibold text-sm transition-colors"
        >
          New Project +
        </button>
      </div>
      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProject(null);
        }}
        mode={editingProject ? 'edit' : 'create'}
        projectId={editingProject?.id}
        onProjectCreated={handleProjectCreated}
      />
    </>
  );
}

